import PdfViewer from "../reader/components/PdfViewer";
import GradingViewBar from "../reader/components/GradingViewBar";
import Canvas from "../canvas/components/Canvas";
import CanvasGradingViewBar from "../canvas/components/CanvasGradingViewBar";
import CategoryEditModal from "../navbar/components/CategoryEditModal";
import StopWatch from "../navbar/components/StopWatch";

import { useState, useEffect, useRef, useCallback } from "react";
import { Box, useMediaQuery, CircularProgress, Typography } from "@mui/material";

import "@/styles/ArgStudio.css";

import TourHost from "@features/onboarding/components/TourHost";
import HintHost from "@features/onboarding/components/HintHost";
import EmptyWorkspaceState from "@features/onboarding/components/EmptyWorkspaceState";
import { selectReadings } from "@store/selectors";
import { selectIsSeedingSample } from "@features/onboarding/state/onboardingSlice";

import { useAppDispatch, useAppSelector } from "@store/hooks";
import ArgNavbar from "../navbar/components/ArgNavbar";
import ArgSidebar from "../sidebar/components/ArgSidebar";
import ViewZoomToolbar from "../navbar/components/ViewZoomToolbar";
import useInterval from "@shared/hooks/useInterval";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import { useAssessmentAutoSave } from "../hooks/useAssessmentAutoSave";
import { useCanvasAssessmentAutoSave } from "../hooks/useCanvasAssessmentAutoSave";
import { resetCanvas } from "@store/slices/workspaceSlice";
import { saveResetHighlights } from "@store/thunks/workspaceThunks";
import { bootstrapDemoSession } from "@store/thunks/demoThunks";
import { selectIsDemoMode } from "@store/slices/viewSlice";
import {
  selectSelectedReadingId,
  selectSelectedCanvasId,
  selectSelectedPaperId,
  selectViewedHighlights,
  selectCurrentPaperState,
} from "@store/selectors";
import EditorModal from "../paper-editor/components/EditorModal";
import ResetWarningModal from "@shared/components/ResetWarningModal";
import Konva from "konva";
import { updateCanvasUserData, updatePaper } from "@services/api";

const defaultSidebarWidth = 400;

function ArgStudioScreen() {
  const userState = useAppSelector((state) => state.user);
  const themeState = useAppSelector((state) => state.theme);
  const workspaceState = useAppSelector((state) => state.workspace);
  const viewState = useAppSelector((state) => state.view);
  const isWorkspaceDataLoading = useAppSelector((state) => state.loading.isWorkspaceDataLoading);
  const workspaceReadings = useAppSelector(selectReadings);
  const isSeedingSample = useAppSelector(selectIsSeedingSample);
  const authChecked = useAppSelector((state) => state.auth.authChecked);

  // Use new selectors for resource IDs and data
  const selectedReadingId = useAppSelector(selectSelectedReadingId);
  const selectedCanvasId = useAppSelector(selectSelectedCanvasId);
  const selectedPaperId = useAppSelector(selectSelectedPaperId);
  const highlights = useAppSelector(selectViewedHighlights);
  const paperState = useAppSelector(selectCurrentPaperState);
  const isDemoMode = useAppSelector(selectIsDemoMode);

  // Enable keyboard shortcuts
  useKeyboardShortcuts();

  // Stage 5: debounced auto-save of the contributor's assessment record.
  // No-op for the workspace owner and when in grading view (see hook).
  useAssessmentAutoSave();
  // Stage 6.6: parallel auto-save for the canvas assessment.
  useCanvasAssessmentAutoSave();

  const [isPdfLoaded, setIsPdfLoaded] = useState(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(0);
  const [destinationPage, setDestinationPage] = useState<number>(1);

  const isNonMobile = useMediaQuery("(min-width: 600px)");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(defaultSidebarWidth);
  const [scale, setScale] = useState<string>("auto");
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const stageRef = useRef<Konva.Stage>(null);

  const [canvasOn, setCanvasOn] = useState<boolean>(false);

  const dispatch = useAppDispatch();


  const [draggedHighlightId, setDraggedHighlightId] = useState<string>("");

  // Sync local state with viewSlice state
  useEffect(() => {
    setCanvasOn(viewState.baseView === "canvas");
  }, [viewState.baseView]);

  useEffect(() => {
    const handleViewportResize = () => {
      setSidebarWidth((prev) => Math.min(prev, window.innerWidth / 2));
    };
    window.addEventListener("resize", handleViewportResize);
    return () => window.removeEventListener("resize", handleViewportResize);
  }, []);

  useEffect(() => {
    setIsEditorOpen(viewState.paperEditorMode !== "hidden");
  }, [viewState.paperEditorMode]);

  useInterval(async () => {
    // TODO: add update for workspace data
    if (!userState.userId) return; // don't save if not logged in

    if (isEditorOpen) {
      if (!selectedPaperId) return; // don't save if no paper selected
      const result = await updatePaper(
        workspaceState.workspace_id,
        selectedPaperId,
        paperState
      );
      if (!result.success) {
        console.error("Error saving editor:", result.error.message);
      }
    } else if (canvasOn && selectedCanvasId) {
      // Canvas auto-save is now handled by thunks when switching resources
      // This interval save is a fallback for periodic saves
      const currentCanvas = workspaceState.canvases.find(
        (c) => c.canvas_id === selectedCanvasId
      );
      if (currentCanvas?.canvas_data) {
        // Stage 6.7: interval-save now goes through the per-user
        // canvas_user_data endpoint, matching saveCanvasIfDirty.
        const result = await updateCanvasUserData(
          workspaceState.workspace_id,
          selectedCanvasId,
          currentCanvas.canvas_data
        );
        if (!result.success) {
          console.error("Error updating canvas:", result.error.message);
        }
      }
    }
    // Stage 3.6: reading-highlight persistence is per-mutation via the
    // save*Highlight thunks. The legacy interval save for highlights was
    // a no-op against the now-orphaned readings.reading_data column and
    // has been removed.
  });

  useEffect(() => {
    // Don't bootstrap the demo until we've confirmed there's no real session.
    // Otherwise a logged-in user's cold refresh races AuthContext.getSession()
    // and ends up with isDemoMode=true even though their real userId resolves.
    if (!authChecked) return;
    if (!userState.userId) {
      if (!isDemoMode) {
        dispatch(bootstrapDemoSession());
      }
      setIsPdfLoaded(true);
    } else if (!selectedReadingId && !selectedCanvasId) {
      setIsPdfLoaded(false);
    } else {
      setIsPdfLoaded(true);
    }
  }, [dispatch, authChecked, userState.userId, isDemoMode, selectedReadingId, selectedCanvasId]);

  useEffect(() => {
    const pdfColor = document.body.style;

    if (themeState.mode === "light") {
      pdfColor.setProperty("--background-color", "#f5f5f5");
      pdfColor.setProperty("--assessment-ghost-stroke", "rgba(40, 40, 40, 0.75)");
    } else {
      pdfColor.setProperty("--background-color", "#989898FF");
      pdfColor.setProperty("--assessment-ghost-stroke", "rgba(240, 240, 240, 0.85)");
    }
  }, [themeState]);

  const handlePageIncrease = () => {
    if (currentPage < pageCount) {
      setDestinationPage(pageCount);
    }
  };

  const handlePageDecrease = () => {
    if (currentPage > 1) {
      setDestinationPage(1);
    }
  };

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= pageCount) {
      setDestinationPage(page);
    }
  };

  const handleHighlightDragStart = (id: string) => () =>
    setDraggedHighlightId(id);

  const toggleCanvas = () => {
    // This function is kept for backward compatibility but is no longer used
    // View switching is now handled by BaseViewToggle in ArgNavbar
    setCanvasOn((prev) => !prev);
  };

  const handleResetCanvas = () => {
    dispatch(resetCanvas());
  };

  const handleResetHighlights = () => {
    dispatch(saveResetHighlights());
  };

  const handleResetDemo = () => {
    dispatch(bootstrapDemoSession());
  };

  const handleExportClick = useCallback(() => {
    if (!stageRef.current) {
      return;
    }

    const dataUrl = stageRef.current.toDataURL({ pixelRatio: 3 });

    let link = document.createElement("a");
    link.download = "canvasImage.png";
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  return (
    <Box
      display={isNonMobile ? "flex" : "block"}
      width="100%"
      height="100%"
      position="relative"
      onContextMenu={(e) => e.preventDefault()}
    >
      <ArgSidebar
        canvasOn={canvasOn}
        currentPage={currentPage}
        handlePageIncrease={handlePageIncrease}
        handlePageDecrease={handlePageDecrease}
        handlePageChange={handlePageChange}
        pageCount={pageCount}
        isNonMobile={isNonMobile}
        drawerWidth={`${sidebarWidth}px`}
        setSidebarWidth={setSidebarWidth}
        isSidebarOpen={isSidebarOpen}
        closeSidebar={() => setIsSidebarOpen(false)}
        handleHighlightDragStart={handleHighlightDragStart}
      />
      <ArgNavbar
        canvasOn={canvasOn}
        isNonMobile={isNonMobile}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        sidebarWidth={sidebarWidth}
        onExportClick={handleExportClick}
        modals={(modalVisible, setModalVisible) => [
          <CategoryEditModal
            modalVisible={modalVisible}
            setModalVisible={setModalVisible}
            key="categoryEdit"
          />,
          <ResetWarningModal
            modalVisible={modalVisible}
            setModalVisible={setModalVisible}
            warningText="This will reset all of your highlights. Are you sure you want to
            proceed?"
            key="resetHighlights"
            modalType="resetHighlights"
            reset={handleResetHighlights}
          />,
          <ResetWarningModal
            modalVisible={modalVisible}
            setModalVisible={setModalVisible}
            warningText="This will reset the canvas. Are you sure you want to proceed?"
            key="canvasReset"
            modalType="resetCanvas"
            reset={handleResetCanvas}
          />,
          <ResetWarningModal
            modalVisible={modalVisible}
            setModalVisible={setModalVisible}
            warningText="This will reset the demo to its original highlights and canvas. Your in-session changes will be discarded. Are you sure?"
            key="resetDemo"
            modalType="resetDemo"
            reset={handleResetDemo}
          />,
          <StopWatch
            modalVisible={modalVisible}
            setModalVisible={setModalVisible}
            key="watch"
          />,
        ]}
      />
      <HintHost />
      <TourHost
        isSidebarOpen={isSidebarOpen}
        setSidebarOpen={setIsSidebarOpen}
        // A reading actually open — not merely "something selected". A
        // fresh account's auto-created canvas must not start a tour
        // whose steps point at reading content.
        contentReady={
          isPdfLoaded && !!selectedReadingId && workspaceReadings.length > 0
        }
      />
      {isPdfLoaded && (workspaceReadings.length > 0 || !userState.userId) ? (
        <>
          <PdfViewer
            scale={scale}
            isSidebarOpen={isSidebarOpen}
            sidebarWidth={sidebarWidth}
            destinationPage={destinationPage}
            getPageCount={(pageCount) => setPageCount(pageCount)}
            getCurrentPage={(currentPage) => setCurrentPage(currentPage)}
            canvasOn={canvasOn}
          />
          <ViewZoomToolbar
            scale={scale}
            setScale={setScale}
            canvasOn={canvasOn}
          />
          {/* The grading bar floats over the PDF/canvas — except when the
              sidebar is open in Evaluate mode, where it docks inline under
              the Evaluate heading instead (so it isn't occluded). */}
          {!(isSidebarOpen && viewState.sidebarMode === "evaluate") &&
            (canvasOn ? <CanvasGradingViewBar /> : <GradingViewBar />)}
        </>
      ) : isWorkspaceDataLoading ? (
        <Box
          width={isNonMobile ? "100%" : "550px"}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "40vh",
            gap: 2,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="body1" color="text.secondary">
            Loading your workspace...
          </Typography>
        </Box>
      ) : isSeedingSample ? (
        <Box
          width={isNonMobile ? "100%" : "550px"}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "40vh",
            gap: 2,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="body1" color="text.secondary">
            Setting up a sample reading for you...
          </Typography>
        </Box>
      ) : userState.userId && workspaceReadings.length === 0 && !canvasOn ? (
        // Reading mode only: the canvas is usable without readings, so
        // a "no readings" overlay there is wrong. The wrapper centers
        // within the *visible* pane — the flex row's sidebar slot keeps
        // its width when closed, so an in-flow child would center
        // 200px right of where the pane appears to be.
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            left: isSidebarOpen ? `${sidebarWidth}px` : 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            // The overlay spans the pane (navbar included) purely for
            // centering; let clicks fall through everywhere except the
            // empty-state content itself.
            pointerEvents: "none",
            "& > *": { pointerEvents: "auto" },
          }}
        >
          <EmptyWorkspaceState />
        </Box>
      ) : null}
      <Canvas
        clearDraggedHighlightId={() => setDraggedHighlightId("")}
        draggedHighlightId={draggedHighlightId}
        canvasOn={canvasOn}
        stageRef={stageRef}
      />
      <EditorModal
        isEditorOpen={isEditorOpen}
        mode={viewState.paperEditorMode}
        stageRef={stageRef}
      />
    </Box>
  );
}

export default ArgStudioScreen;
